/*
 * Theming contract. A component that embeds Link — Breadcrumb, for one — adapts
 * it by setting these on its own block, never by reaching in with a descendant
 * selector such as `.eds-block .eds-link`. Each falls back to the Link default,
 * so an unset property changes nothing.
 *
 *   --eds-link-color              resting text color
 *   --eds-link-color-hover        :hover text color
 *   --eds-link-color-active       :active text color
 *   --eds-link-text-decoration    underline / none
 *   --eds-link-font-weight        weight, when the embedding context is quieter
 *
 * Values assigned to these must still be `--eds--*` tokens (constitution §2).
 */

/* Base */
.eds-link {
  /* Inherit when unset: a Link with no variant class takes the local color. */
  color: var(--eds-link-color, inherit);
  cursor: pointer;
  display: inline;
  transition-duration: var(--eds--transition-duration-moderate);
  transition-property: color;
  transition-timing-function: ease-out;
}

/* Variants */
.eds-link--variant-default {
  color: var(--eds-link-color, var(--eds--color--fg-brand-default));
}

.eds-link--variant-default.eds-link--m {
  font: var(--eds--typography--link-m-font);
  /* After the `font` shorthand, which would otherwise reset the weight. */
  font-weight: var(
    --eds-link-font-weight,
    var(--eds--typography--link-m-font-weight)
  );
  text-decoration: var(
    --eds-link-text-decoration,
    var(--eds--typography--link-m-text-decoration)
  );
}

.eds-link--variant-default.eds-link--s {
  font: var(--eds--typography--link-s-font);
  font-weight: var(
    --eds-link-font-weight,
    var(--eds--typography--link-s-font-weight)
  );
  text-decoration: var(
    --eds-link-text-decoration,
    var(--eds--typography--link-s-text-decoration)
  );
}

.eds-link--variant-subdued {
  color: var(--eds--color--fg-default);
  text-decoration: underline;
  text-underline-position: from-font;
}

.eds-link--variant-subdued.eds-link--m {
  font: var(--eds--typography--body-semi-m-font);
  text-decoration: underline;
}

.eds-link--variant-subdued.eds-link--s {
  font: var(--eds--typography--body-semi-s-font);
  text-decoration: underline;
}

.eds-link--external {
  display: inline;
}

.eds-link--icon {
  align-items: center;
  display: inline-flex;
  height: 1lh;
  margin-inline-start: var(--eds--spacing-4);
  vertical-align: -0.2rem;
}

.eds-link--external.eds-link--truncate {
  align-items: center;
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
}

.eds-link--truncate .eds-link--icon {
  flex: none;
}

/* States */
.eds-link--variant-default:hover {
  color: var(--eds-link-color-hover, var(--eds--palette-blue-600));
}

.eds-link--variant-default:active {
  color: var(
    --eds-link-color-active,
    color-mix(in srgb, var(--eds--palette-blue-600) 80%, black)
  );
}

.eds-link--variant-subdued:hover,
.eds-link--variant-subdued:active {
  color: var(--eds--color--fg-hint);
}

.eds-link:focus-visible {
  border-radius: var(--eds--radii-4);
  outline: none;
  box-shadow: var(--eds--interaction-focus-ring);
}

.eds-link > * {
  color: inherit;
}

.eds-link:hover .eds-icon {
  color: inherit;
}

.eds-link .eds-text {
  font: inherit;
  text-decoration: underline;
}
